import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getProjectBySlug } from '../data/projects';
import {
  IconRocket, IconAI, IconPalette, IconZap, IconDollar, IconGear, IconSearch, IconGlobe,
  IconCloud, IconShield, IconClipboard, IconTrash, IconCamera, IconSparkles, IconAudio,
  IconCollection, IconDevices, IconWine, IconUsers, IconMic, IconHeadphones, IconDocument,
  IconShare, IconWaveform, IconTerminal, IconTrophy, IconBarChart, IconAward, IconWatch,
  IconHeart, IconBell
} from '../components/Icons';
import './ProjectDetail.css';

const ProjectDetail: React.FC = () => {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const project = projectSlug ? getProjectBySlug(projectSlug) : undefined;

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const getIcon = (iconKey: string) => {
    switch (iconKey) {
      case 'rocket': return <IconRocket />;
      case 'bot': return <IconAI />;
      case 'palette': return <IconPalette />;
      case 'zap': return <IconZap />;
      case 'dollar': return <IconDollar />;
      case 'settings': return <IconGear />;
      case 'search': return <IconSearch />;
      case 'image': return <IconPalette />;
      case 'world': return <IconGlobe />;
      case 'cloud': return <IconCloud />;
      case 'shield': return <IconShield />;
      case 'clipboard': return <IconClipboard />;
      case 'trash': return <IconTrash />;
      case 'camera': return <IconCamera />;
      case 'sparkles': return <IconSparkles />;
      case 'audio': return <IconAudio />;
      case 'collection': return <IconCollection />;
      case 'devices': return <IconDevices />;
      case 'wine': return <IconWine />;
      case 'mic': return <IconMic />;
      case 'headphones': return <IconHeadphones />;
      case 'document': return <IconDocument />;
      case 'users': return <IconUsers />;
      case 'share': return <IconShare />;
      case 'waveform': return <IconWaveform />;
      case 'terminal': return <IconTerminal />;
      case 'trophy': return <IconTrophy />;
      case 'bar-chart-2': return <IconBarChart />;
      case 'award': return <IconAward />;
      case 'watch': return <IconWatch />;
      case 'heart': return <IconHeart />;
      case 'bell': return <IconBell />;
      default: return null;
    }
  };

  return (
    <div className="project-detail-page mesh-gradient">
      <section className="breadcrumb-section">
        <div className="breadcrumb-container">
          <Link to="/projects" className="breadcrumb-link">
            <svg className="breadcrumb-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 3.5l-7 5.25v6.75h5v-5h4v5h5V8.75L8 3.5z" />
            </svg>
            Projects
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{project.name}</span>
        </div>
      </section>

      <section className="project-detail-hero animate-fade-in-up">
        <div className="project-detail-container">
          <div className="project-card featured glass">
            {project.heroImage && (
              <div className="project-hero-image">
                <img src={project.heroImage} alt={project.name} />
              </div>
            )}
            <div className="project-header">
              {!project.heroImage && (
                <div className="project-icon icon-glow">
                  {getIcon(project.icon)}
                </div>
              )}
              <div className="project-title-section">
                <h2>{project.name}</h2>
                <p className="project-tagline">{project.tagline}</p>
              </div>
              {project.links.find(l => l.type === 'github') && (
                <a
                  href={project.links.find(l => l.type === 'github')!.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="github-badge glass"
                >
                  <svg className="github-icon" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
                  </svg>
                  View on GitHub
                </a>
              )}
            </div>

            <div className="project-content">
              <div className="project-description">
                <p dangerouslySetInnerHTML={{ __html: project.fullDescription.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              </div>

              <div className="tech-stack">
                <h4>Tech Stack</h4>
                <div className="tech-badges">
                  {project.techStack.map((tech, index) => (
                    <span key={index} className="tech-badge">{tech}</span>
                  ))}
                </div>
              </div>

              <div className="features-grid">
                {project.features.map((feature, index) => (
                  <div key={index} className="feature-column">
                    <div className="feature-header">
                      <span className="feature-icon">{getIcon(feature.icon)}</span>
                      <h4>{feature.title}</h4>
                    </div>
                    <ul>
                      {feature.items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="use-cases">
                <h4>Perfect For</h4>
                <div className="use-case-tags">
                  {project.useCases.map((useCase, index) => (
                    <span key={index} className="use-case-tag">{useCase}</span>
                  ))}
                </div>
              </div>

              {project.screenshots && project.screenshots.length > 0 && (
                <div className="screenshot-gallery">
                  <h4>Screenshots</h4>
                  <div className="screenshot-grid">
                    {project.screenshots.map((src, index) => (
                      <div key={index} className="screenshot-item">
                        <img
                          src={src}
                          alt={`${project.name} screenshot ${index + 1}`}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {project.installations.map((installation, index) => (
                <div key={index} className="installation" style={index > 0 ? { marginTop: '1.5rem' } : {}}>
                  {installation.title && <h4>{installation.title}</h4>}
                  {installation.description && (
                    <p dangerouslySetInnerHTML={{ __html: installation.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  )}
                  {installation.command && (
                    <div className="code-block">
                      <code>{installation.command}</code>
                    </div>
                  )}
                  {installation.codeBlock && (
                    <div className="code-block">
                      <pre>{installation.codeBlock}</pre>
                    </div>
                  )}
                  {installation.note && (
                    <p style={{ marginTop: installation.command || installation.codeBlock ? '0.75rem' : '0' }}>
                      {installation.note.includes('`') ? (
                        <span dangerouslySetInnerHTML={{
                          __html: installation.note.replace(/`(.*?)`/g, '<code>$1</code>')
                        }} />
                      ) : (
                        installation.note
                      )}
                      {installation.note.includes('releases page') && (
                        <> <a href={project.links.find(l => l.url.includes('/releases'))?.url} target="_blank" rel="noopener noreferrer">releases page</a>.</>
                      )}
                    </p>
                  )}
                </div>
              ))}

              <div className="project-actions">
                {project.links.slice(0, 2).map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={index === 0 ? 'btn-primary' : 'btn-secondary'}
                  >
                    {link.type === 'github' && (
                      <svg className="btn-icon" viewBox="0 0 16 16" fill="currentColor">
                        <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
                      </svg>
                    )}
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
